import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Dropdown,
  Option,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Tag,
  Field,
  Textarea,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Toast,
  ToastTitle,
  useToastController,
} from '@fluentui/react-components';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { GLOBAL_TOASTER_ID } from '../components/ThemedProvider.js';
import { useConfirm } from '../components/ConfirmDialog.js';
import { useAuthStore } from '../stores/authStore.js';
import {
  listApuLibrary,
  getApuLibraryEntry,
  updateApuLibraryEntry,
  deleteApuLibraryEntry,
  type ApuLibraryListItem,
  type ApuLibraryDetail,
} from '../api/staffApuLibrary.js';
import { ApiClientError } from '../api/client.js';
import { fmtDate } from '../lib/staffUsageFormat.js';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  content: { flex: 1, padding: '24px 32px', maxWidth: '1280px', width: '100%', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '16px', flexWrap: 'wrap' },
  title: { fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightSemibold },
  subtitle: { color: tokens.colorNeutralForeground3, marginBottom: '20px' },
  filters: { display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '16px' },
  tableWrap: { border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, overflow: 'hidden', background: tokens.colorNeutralBackground1 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 14px', background: tokens.colorNeutralBackground2, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap' },
  thRight: { textAlign: 'right' },
  td: { padding: '10px 14px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, fontSize: tokens.fontSizeBase300, verticalAlign: 'top' },
  tdRight: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  rowBtn: { cursor: 'pointer' },
  emptyRow: { padding: '24px 14px', textAlign: 'center', color: tokens.colorNeutralForeground3 },
  dialogFields: { display: 'flex', flexDirection: 'column', gap: '12px' },
  row2: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  jsonArea: { fontFamily: 'monospace', fontSize: tokens.fontSizeBase200 },
  meta: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  section: { marginTop: '12px' },
  sectionTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase200, color: tokens.colorBrandForeground1, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' },
  cuadName: { fontSize: tokens.fontSizeBase300, marginBottom: '4px' },
  dTable: { width: '100%', borderCollapse: 'collapse', fontSize: tokens.fontSizeBase200 },
  dth: { textAlign: 'left', padding: '3px 8px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, color: tokens.colorNeutralForeground3, fontWeight: tokens.fontWeightSemibold },
  dtd: { padding: '3px 8px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}` },
  dtdR: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  rendBox: { background: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusMedium, padding: '8px 12px', fontSize: tokens.fontSizeBase300 },
});

const SOURCE_LABEL: Record<string, string> = { apu_ia: 'APU-IA', ecostos: 'ECOSTOS', manual: 'Manual' };

type Row = Record<string, unknown>;
const num = (v: unknown) => (typeof v === 'number' ? v : Number(v) || 0);
const str = (v: unknown) => (v == null ? '' : String(v));

/** Desglose tipo m1: secciones por tipo de recurso (mano de obra, materiales, equipo, auxiliares). */
function ApuDesglose({ est, dStyles }: { est: Row; dStyles: ReturnType<typeof useStyles> }) {
  const cuadrilla = est['cuadrilla'] as Row | undefined;
  const cuadRecursos = (cuadrilla?.['recursos'] as Row[] | undefined) ?? (cuadrilla?.['componentes'] as Row[] | undefined) ?? [];
  const materiales = (est['materiales'] as Row[] | undefined) ?? [];
  const equipo = (est['equipo'] as Row[] | undefined) ?? [];
  const auxiliares = (est['auxiliares'] as Row[] | undefined) ?? [];
  const herrPct = num(est['herramienta_pct']);

  const Section = ({ title, children }: { title: string; children: ReactNode }) => (
    <div className={dStyles.section}>
      <div className={dStyles.sectionTitle}>{title}</div>
      {children}
    </div>
  );

  return (
    <div>
      {(cuadrilla || cuadRecursos.length > 0) && (
        <Section title="Mano de obra / Cuadrilla">
          <div className={dStyles.cuadName}>{str(cuadrilla?.['tipo_cuadrilla']) || 'Cuadrilla'}</div>
          {cuadRecursos.length > 0 && (
            <table className={dStyles.dTable}>
              <tbody>
                {cuadRecursos.map((r, i) => (
                  <tr key={i}>
                    <td className={dStyles.dtd}>{str(r['descripcion'] ?? r['categoria'] ?? r['clave'])}</td>
                    <td className={`${dStyles.dtd} ${dStyles.dtdR}`}>{r['jornadas'] != null ? `${num(r['jornadas'])} jor` : num(r['cantidad'])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      )}

      {materiales.length > 0 && (
        <Section title={`Materiales (${materiales.length})`}>
          <table className={dStyles.dTable}>
            <thead><tr><th className={dStyles.dth}>Descripción</th><th className={dStyles.dth}>Unidad</th><th className={`${dStyles.dth} ${dStyles.dtdR}`}>Cantidad</th></tr></thead>
            <tbody>
              {materiales.map((m, i) => (
                <tr key={i}>
                  <td className={dStyles.dtd}>{str(m['descripcion_normalizada'] ?? m['descripcion'])}</td>
                  <td className={dStyles.dtd}>{str(m['unidad'])}</td>
                  <td className={`${dStyles.dtd} ${dStyles.dtdR}`}>{num(m['cantidad_neta'] ?? m['cantidad'])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {equipo.length > 0 && (
        <Section title={`Equipo (${equipo.length})`}>
          <table className={dStyles.dTable}>
            <tbody>
              {equipo.map((e, i) => (
                <tr key={i}>
                  <td className={dStyles.dtd}>{str(e['descripcion'] ?? e['descripcion_normalizada'])}</td>
                  <td className={dStyles.dtd}>{str(e['unidad'])}</td>
                  <td className={`${dStyles.dtd} ${dStyles.dtdR}`}>{num(e['cantidad'])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {herrPct > 0 && (
        <Section title="Herramienta / indirectos"><div className={dStyles.cuadName}>{herrPct}% sobre mano de obra</div></Section>
      )}

      {auxiliares.length > 0 && (
        <Section title={`Auxiliares (${auxiliares.length})`}>
          <table className={dStyles.dTable}>
            <tbody>
              {auxiliares.map((a, i) => (
                <tr key={i}>
                  <td className={dStyles.dtd}>{str(a['descripcion'])}</td>
                  <td className={dStyles.dtd}>{str(a['unidad'])}</td>
                  <td className={`${dStyles.dtd} ${dStyles.dtdR}`}>{num(a['cantidad'])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </div>
  );
}

export function StaffApuLibraryPage() {
  const styles = useStyles();
  const confirm = useConfirm();
  const tenant = useAuthStore((s) => s.tenant);
  const isStaff = !!tenant?.isEcosoftOwned && (tenant.role === 'admin' || tenant.role === 'owner');
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);

  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [items, setItems] = useState<ApuLibraryListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<ApuLibraryDetail | null>(null);
  const [estructuraText, setEstructuraText] = useState('');
  const [showJson, setShowJson] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!isStaff) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listApuLibrary({
      q: search || undefined,
      source: (sourceFilter || undefined) as 'apu_ia' | 'ecostos' | 'manual' | undefined,
      limit: 100,
    })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiClientError ? err.message : 'Error al cargar la biblioteca');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isStaff, search, sourceFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openDetail = async (id: string) => {
    try {
      const detail = await getApuLibraryEntry(id);
      setSelected(detail);
      setEstructuraText(JSON.stringify(detail.estructura ?? {}, null, 2));
      setShowJson(false);
    } catch (err) {
      dispatchToast(
        <Toast><ToastTitle>{err instanceof ApiClientError ? err.message : 'Error al abrir'}</ToastTitle></Toast>,
        { intent: 'error' },
      );
    }
  };

  const save = async () => {
    if (!selected) return;
    let estructura: Record<string, unknown> | undefined;
    try {
      estructura = JSON.parse(estructuraText) as Record<string, unknown>;
    } catch {
      dispatchToast(<Toast><ToastTitle>La estructura no es JSON válido</ToastTitle></Toast>, { intent: 'error' });
      return;
    }
    setSaving(true);
    try {
      await updateApuLibraryEntry(selected.id, {
        conceptoDescripcion: selected.concepto_descripcion,
        unidad: selected.unidad,
        rendimientoBase: Number(selected.rendimiento_base),
        rendimientoBaseUnidad: selected.rendimiento_base_unidad,
        estructura,
      });
      dispatchToast(<Toast><ToastTitle>Entrada actualizada</ToastTitle></Toast>, { intent: 'success' });
      setSelected(null);
      load();
    } catch (err) {
      dispatchToast(
        <Toast><ToastTitle>{err instanceof ApiClientError ? err.message : 'Error al guardar'}</ToastTitle></Toast>,
        { intent: 'error' },
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected) return;
    if (!(await confirm({
      title: 'Eliminar entrada',
      message: '¿Eliminar esta entrada del pool global? Dejará de reutilizarse.',
      confirmLabel: 'Eliminar',
      destructive: true,
    }))) return;
    setSaving(true);
    try {
      await deleteApuLibraryEntry(selected.id);
      dispatchToast(<Toast><ToastTitle>Entrada eliminada</ToastTitle></Toast>, { intent: 'success' });
      setSelected(null);
      load();
    } catch (err) {
      dispatchToast(
        <Toast><ToastTitle>{err instanceof ApiClientError ? err.message : 'Error al eliminar'}</ToastTitle></Toast>,
        { intent: 'error' },
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isStaff) return <Navigate to="/" replace />;

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs
        items={[
          { label: 'Inicio', to: '/projects' },
          { label: 'Staff' },
          { label: 'Biblioteca de análisis con IA' },
        ]}
      />
      <div className={styles.content}>
        <div className={styles.header}>
          <Text className={styles.title}>Biblioteca de análisis con IA</Text>
          <Text className={styles.meta}>{total} entradas en el pool global</Text>
        </div>
        <Text as="p" className={styles.subtitle}>
          Pool global compartido de análisis con IA aprobados (curaduría Ecosoft). Invisible para clientes.
        </Text>

        <div className={styles.filters}>
          <Field label="Buscar concepto" className={styles.row2}>
            <Input value={search} onChange={(_e, d) => setSearch(d.value)} placeholder="Ej. castillo de concreto" />
          </Field>
          <Field label="Origen">
            <Dropdown
              value={sourceFilter ? SOURCE_LABEL[sourceFilter] : 'Todos'}
              selectedOptions={[sourceFilter]}
              onOptionSelect={(_e, d) => setSourceFilter(d.optionValue ?? '')}
            >
              <Option value="">Todos</Option>
              <Option value="apu_ia">APU-IA</Option>
              <Option value="ecostos">ECOSTOS</Option>
              <Option value="manual">Manual</Option>
            </Dropdown>
          </Field>
        </div>

        {error && (
          <MessageBar intent="error" style={{ marginBottom: 16 }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {loading ? (
          <Spinner label="Cargando…" />
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Concepto</th>
                  <th className={styles.th}>Unidad</th>
                  <th className={`${styles.th} ${styles.thRight}`}>Rend. base</th>
                  <th className={styles.th}>Origen</th>
                  <th className={`${styles.th} ${styles.thRight}`}>Usos</th>
                  <th className={styles.th}>Embedding</th>
                  <th className={styles.th}>Agregado</th>
                  <th className={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {(items ?? []).length === 0 ? (
                  <tr>
                    <td className={styles.emptyRow} colSpan={8}>
                      Sin entradas todavía. La biblioteca se llena al aprobar análisis con IA.
                    </td>
                  </tr>
                ) : (
                  (items ?? []).map((it) => (
                    <tr key={it.id}>
                      <td className={styles.td}>{it.concepto_descripcion}</td>
                      <td className={styles.td}>{it.unidad}</td>
                      <td className={`${styles.td} ${styles.tdRight}`}>
                        {Number(it.rendimiento_base)} {it.rendimiento_base_unidad}
                      </td>
                      <td className={styles.td}><Tag size="small">{SOURCE_LABEL[it.source] ?? it.source}</Tag></td>
                      <td className={`${styles.td} ${styles.tdRight}`}>{it.usage_count}</td>
                      <td className={styles.td}>{it.has_embedding ? '✓' : '—'}</td>
                      <td className={styles.td}>{fmtDate(it.created_at)}</td>
                      <td className={styles.td}>
                        <Button size="small" appearance="secondary" onClick={() => openDetail(it.id)}>
                          Ver / Editar
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(_e, d) => !d.open && setSelected(null)}>
        <DialogSurface style={{ maxWidth: 720 }}>
          <DialogBody>
            <DialogTitle>Editar APU de biblioteca</DialogTitle>
            <DialogContent>
              {selected && (
                <div className={styles.dialogFields}>
                  <Field label="Descripción del concepto">
                    <Textarea
                      value={selected.concepto_descripcion}
                      onChange={(_e, d) => setSelected({ ...selected, concepto_descripcion: d.value })}
                    />
                  </Field>
                  <div className={styles.row2}>
                    <Field label="Unidad">
                      <Input value={selected.unidad} onChange={(_e, d) => setSelected({ ...selected, unidad: d.value })} />
                    </Field>
                    <Field label="Rendimiento base">
                      <Input
                        type="number"
                        value={String(selected.rendimiento_base)}
                        onChange={(_e, d) => setSelected({ ...selected, rendimiento_base: d.value })}
                      />
                    </Field>
                    <Field label="Unidad rend.">
                      <Input
                        value={selected.rendimiento_base_unidad}
                        onChange={(_e, d) => setSelected({ ...selected, rendimiento_base_unidad: d.value })}
                      />
                    </Field>
                  </div>
                  <div className={styles.rendBox}>
                    <strong>Rendimiento base:</strong> {Number(selected.rendimiento_base)} {selected.rendimiento_base_unidad}
                    <span className={styles.meta}> · los factores se recalculan por obra al reutilizar</span>
                  </div>

                  <div>
                    <div className={styles.sectionTitle}>Desglose (receta)</div>
                    {(() => {
                      let est: Row = {};
                      try { est = JSON.parse(estructuraText) as Row; } catch { /* editor avanzado lo valida */ }
                      return <ApuDesglose est={est} dStyles={styles} />;
                    })()}
                  </div>

                  <Button appearance="transparent" size="small" onClick={() => setShowJson((v) => !v)} style={{ alignSelf: 'flex-start', paddingLeft: 0 }}>
                    {showJson ? 'Ocultar' : 'Editar composición (JSON avanzado)'}
                  </Button>
                  {showJson && (
                    <Field hint="Solo la receta estable; los factores se recalculan por obra.">
                      <Textarea
                        className={styles.jsonArea}
                        value={estructuraText}
                        onChange={(_e, d) => setEstructuraText(d.value)}
                        rows={14}
                      />
                    </Field>
                  )}
                  <Text className={styles.meta}>
                    Origen: {SOURCE_LABEL[selected.source] ?? selected.source} · usos: {selected.usage_count} ·
                    {selected.has_embedding ? ' con embedding' : ' sin embedding (se re-embeberá al editar la descripción)'}
                  </Text>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={remove} disabled={saving} style={{ marginRight: 'auto' }}>
                Eliminar
              </Button>
              <Button appearance="secondary" onClick={() => setSelected(null)} disabled={saving}>
                Cancelar
              </Button>
              <Button appearance="primary" onClick={save} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
